import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import { makeStyles } from "@material-ui/core/styles";

// Nạp toàn bộ ảnh trong từng thư mục (assets/image/kvc-gallery/*)
const importAll = (ctx) =>
  ctx
    .keys()
    .sort()
    .map((k) => {
      const m = ctx(k);
      return m && m.default ? m.default : m;
    });

const TOP = importAll(
  require.context(
    "../../../assets/image/kvc-gallery/top",
    false,
    /\.(jpe?g|png|webp)$/
  )
);
const GIAI_THUONG = importAll(
  require.context(
    "../../../assets/image/kvc-gallery/giaithuong",
    false,
    /\.(jpe?g|png|webp)$/
  )
);
const BAO_CHI = importAll(
  require.context(
    "../../../assets/image/kvc-gallery/baochi",
    false,
    /\.(jpe?g|png|webp)$/
  )
);

const useStyles = makeStyles(() => ({
  section: { marginTop: 40 },
  title: {
    textAlign: "center",
    fontFamily: "WorkSans-ExtraBold",
    marginBottom: 16,
  },
  carousel: { position: "relative", display: "flex", alignItems: "center" },
  viewport: { overflow: "hidden", width: "100%" },
  track: { display: "flex", transition: "transform 0.5s ease" },
  slide: { boxSizing: "border-box", padding: 8, cursor: "pointer" },
  slideInner: {
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
    },
  },
  slideImg: {
    width: "100%",
    height: 220,
    objectFit: "cover",
    display: "block",
  },
  carouselBtn: {
    flex: "0 0 auto",
    color: "#fff",
    backgroundColor: "#ee6064",
    margin: "0 4px",
    "&:hover": { backgroundColor: "#d84f53" },
    "&.Mui-disabled": { backgroundColor: "#e0c3c4", color: "#fff" },
  },
  lightboxPaper: {
    position: "relative",
    backgroundColor: "rgba(18,18,18,0.96)",
    overflow: "hidden",
    margin: 16,
  },
  lightboxImg: {
    width: "min(90vw, 900px)",
    aspectRatio: "3 / 2",
    maxHeight: "80vh",
    objectFit: "cover",
    display: "block",
    backgroundColor: "#000",
  },
  closeBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 2,
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.35)",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
  },
  navBtn: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.35)",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
  },
}));

// Slider tự chạy chậm (4.5s), quay vòng, tạm dừng khi rê chuột; bấm ảnh xem lớn.
function Slider({ title, items, classes }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const perView = isXs ? 1 : isSm ? 2 : 3;

  const len = items.length;
  const canLoop = len > perView;

  // pos: chỉ số logic (có thể tạm tới -1 hoặc len khi đang quay vòng)
  const [pos, setPos] = useState(0);
  const [anim, setAnim] = useState(true);
  const [paused, setPaused] = useState(false);
  const [openIndex, setOpenIndex] = useState(-1);

  // Chỉ cho bước khi đang ở dải ảnh thật [0, len-1]. Nếu đang ở ảnh clone
  // (pos = len hoặc -1, tức đang chờ "snap" về vị trí thật) thì bỏ qua click
  // để pos không vượt ra ngoài mảng extended -> tránh ô trắng bị kẹt ở cuối.
  const prev = () =>
    setPos((p) => {
      if (!canLoop) return Math.max(0, p - perView);
      if (p < 0 || p > len - 1) return p;
      return p - 1;
    });
  const next = () =>
    setPos((p) => {
      if (!canLoop) return Math.min(p + perView, Math.max(0, len - perView));
      if (p < 0 || p > len - 1) return p;
      return p + 1;
    });

  // Tự chạy chậm (không tăng quá len để tránh vượt biên khi đang chờ snap)
  useEffect(() => {
    if (paused || !canLoop) return undefined;
    const id = setInterval(() => setPos((p) => (p > len - 1 ? p : p + 1)), 4500);
    return () => clearInterval(id);
  }, [paused, canLoop, len]);

  // Vòng lặp vô hạn: chạm clone -> snap về vị trí thật (tắt animation 1 nhịp)
  useEffect(() => {
    if (!canLoop) return undefined;
    if (pos >= len) {
      const t = setTimeout(() => {
        setAnim(false);
        setPos(0);
      }, 520);
      return () => clearTimeout(t);
    }
    if (pos <= -1) {
      const t = setTimeout(() => {
        setAnim(false);
        setPos(len - 1);
      }, 520);
      return () => clearTimeout(t);
    }
    if (!anim) {
      const t = setTimeout(() => setAnim(true), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [pos, anim, canLoop, len]);

  const isOpen = openIndex >= 0;
  const close = useCallback(() => setOpenIndex(-1), []);
  const lbPrev = useCallback(
    () => setOpenIndex((i) => (i <= 0 ? items.length - 1 : i - 1)),
    [items.length]
  );
  const lbNext = useCallback(
    () => setOpenIndex((i) => (i >= items.length - 1 ? 0 : i + 1)),
    [items.length]
  );

  if (!items || len === 0) return null;

  // Ảnh hiển thị: clone perView ảnh ở 2 đầu để cuộn vòng liền mạch
  const lead = canLoop
    ? items
        .slice(len - perView)
        .map((src, i) => ({ src, real: len - perView + i }))
    : [];
  const trail = canLoop
    ? items.slice(0, perView).map((src, i) => ({ src, real: i }))
    : [];
  const extended = [
    ...lead,
    ...items.map((src, i) => ({ src, real: i })),
    ...trail,
  ];
  const translate = (pos + lead.length) * (100 / perView);

  return (
    <Box className={classes.section}>
      <Typography variant="h5" className={classes.title}>
        <b>{title}</b>
      </Typography>
      <Box
        className={classes.carousel}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <IconButton
          className={classes.carouselBtn}
          onClick={prev}
          aria-label="Ảnh trước"
        >
          <NavigateBeforeIcon />
        </IconButton>
        <Box className={classes.viewport}>
          <Box
            className={classes.track}
            style={{
              transform: `translateX(-${translate}%)`,
              transition: anim ? "transform 0.5s ease" : "none",
            }}
          >
            {extended.map((item, index) => (
              <Box
                key={index}
                className={classes.slide}
                style={{ flex: `0 0 ${100 / perView}%` }}
                onClick={() => setOpenIndex(item.real)}
              >
                <Box className={classes.slideInner}>
                  <img
                    src={item.src}
                    alt={`${title} ${item.real + 1}`}
                    loading="lazy"
                    className={classes.slideImg}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <IconButton
          className={classes.carouselBtn}
          onClick={next}
          aria-label="Ảnh sau"
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>

      <Dialog
        open={isOpen}
        onClose={close}
        maxWidth="lg"
        PaperProps={{ className: classes.lightboxPaper }}
      >
        <IconButton className={classes.closeBtn} onClick={close} aria-label="Đóng">
          <CloseIcon />
        </IconButton>
        <IconButton
          className={classes.navBtn}
          style={{ left: 8 }}
          onClick={lbPrev}
          aria-label="Ảnh trước"
        >
          <NavigateBeforeIcon fontSize="large" />
        </IconButton>
        <IconButton
          className={classes.navBtn}
          style={{ right: 8 }}
          onClick={lbNext}
          aria-label="Ảnh sau"
        >
          <NavigateNextIcon fontSize="large" />
        </IconButton>
        {isOpen && (
          <img src={items[openIndex]} alt={title} className={classes.lightboxImg} />
        )}
      </Dialog>
    </Box>
  );
}

export default function IntroGallery() {
  const classes = useStyles();
  return (
    <Box>
      <Slider
        title="Một số hoạt động của Ban thư ký và Hội đồng giải thưởng"
        items={TOP}
        classes={classes}
      />
      <Slider
        title="Một số hình ảnh về lễ trao giải thưởng"
        items={GIAI_THUONG}
        classes={classes}
      />
      <Slider
        title="Truyền thông báo chí về Giải thưởng"
        items={BAO_CHI}
        classes={classes}
      />
    </Box>
  );
}
